import { Space, Tag } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { Account } from 'domains/accountDomain';
import { Contact, EMPTY_ADDR, ContactLevelEnum } from 'domains/orgDomain';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AddrNestedTable from './AddrNestedTable';
import { useParentOrg } from './useParentOrg';

type Props = {
  searchValue: string;
};

const CtAdminTable = ({ searchValue }: Props) => {
  const { t } = useTranslation();
  const curContact = useAppSelector((state) => state.org.curContact);
  const curAddr = useAppSelector((state) => state.org.curAddr);
  const columns: TableColumnsType<Contact> = [
    {
      title: t('common.id'),
      dataIndex: 'id',
      fixed: 'left',
    },
    {
      title: t('platform.org.ct.table.columns.firstName'),
      dataIndex: 'firstName',
    },
    {
      title: t('platform.org.ct.table.columns.lastName'),
      dataIndex: 'lastName',
    },
    {
      title: t('platform.org.ct.table.columns.level'),
      dataIndex: 'contactLevel',
      render: (level: ContactLevelEnum) => (
        <Tag color={level === ContactLevelEnum.ORG ? 'blue' : 'geekblue'}>
          {level}
        </Tag>
      ),
    },
    {
      title: t('platform.org.ct.table.columns.user'),
      dataIndex: 'user',
      render: (user: Account, record: Contact) =>
        record.contactLevel === ContactLevelEnum.USER && user ? (
          <Space direction="vertical" size={0}>
            <span>{`${user.firstName || ''} ${user.lastName || ''}`.trim()}</span>
            <span>{user.email}</span>
          </Space>
        ) : (
          <></>
        ),
    },
    {
      title: t('platform.org.ct.table.columns.title'),
      dataIndex: 'title',
    },
    {
      title: t('platform.org.ct.table.columns.department'),
      dataIndex: 'department',
    },
    {
      title: t('platform.org.ct.table.columns.costCenter'),
      dataIndex: 'costCenter',
    },
    {
      title: t('platform.org.ct.table.columns.company'),
      dataIndex: 'company',
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'createAt',
      valueType: 'time',
    },
    {
      title: t('common.actions'),
      key: 'actions',
      fixed: 'right',
      render: (value, record) => (
        <Space size="small">
          <RevxButton
            type="link"
            size="small"
            key="view"
            onClick={(e) => {
              dispatch.org.setState({ curContact: record });
              e.stopPropagation();
            }}
          >
            {t('common.view')}
          </RevxButton>
          <RevxButton
            type="link"
            key="sub"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              dispatch.org.setState({
                curAddr: {
                  ...EMPTY_ADDR,
                  contactId: record.id,
                },
              });
            }}
          >
            {t('platform.org.ct.table.actions.createAddr')}
          </RevxButton>
        </Space>
      ),
    },
  ];

  const dispatch = useAppDispatch();

  const parentOrg = useParentOrg();

  // table data processing
  const data = useAppSelector((state) => state?.org?.contacts?.content || []);
  const total = useAppSelector(
    (state) => state?.org?.contacts?.totalElements || 0
  );
  const currentPage = useAppSelector((state) => state.org.ctCurrentPage);
  const pageSize = useAppSelector((state) => state.org.ctPageSize);
  const loading = useAppSelector(
    (state) => state.loading.effects.org.getContacts
  );

  useEffect(() => {
    if (parentOrg) {
      dispatch.org.getContacts({
        orgId: parentOrg.id,
        page: { currentPage, pageSize },
      });
    }
  }, [currentPage, dispatch.org, pageSize, parentOrg]);

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          const ctCurrentPage = pagination.current;
          const ctPageSize = pagination.pageSize;
          dispatch.org.setState({ ctCurrentPage, ctPageSize });
          break;
      }
    },
    [dispatch.org]
  );

  return (
    <div hidden={!!curContact || !!curAddr}>
      <RevxTable<Contact>
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={{
          pageSize,
          current: currentPage,
          total,
        }}
        size="small"
        onChange={onTableChange}
        expandable={{
          expandRowByClick: true,
          expandedRowRender: (contact) => (
            <AddrNestedTable
              contactId={contact.id}
              defaultAddressId={contact.defaultAddressId}
            />
          ),
        }}
      />
    </div>
  );
};

export default CtAdminTable;
